/*
 * <welcome-popup> (sections/welcome-popup.liquid): the $20-off sign-up pop-up.
 *
 * Never shown to people Klaviyo already knows (signed up before, clicked our emails/texts),
 * logged-in customers, or visitors arriving from our emails/texts.
 * Remembers each visitor in their browser (localStorage "sunday:welcome-popup"):
 *   signed-up        -> never shown again
 *   closed:<time>    -> the small "you've got $20 off" tab shows; the pop-up itself comes
 *                       back on its own after the "show again" number of days
 * Sign-ups go to Klaviyo (client API, same as the notify-me form) once they've given both their
 * email and phone number. A Klaviyo flow on the list emails/texts their unique code.
 */
const POPUP_KEY = 'sunday:welcome-popup';
const VARIANT_KEY = 'sunday:welcome-popup-version';

class WelcomePopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-popup]');
    this.teaser = this.querySelector('[data-teaser]');
    this.answer = '';
    this.email = '';
    this.variant = this.pickVariant();
    if (this.variant === 'B') {
      this.querySelector('[data-variant-b]').hidden = false;
      this.querySelector('[data-variant-a]').hidden = true;
      this.teaser.querySelector('span').textContent = this.dataset.teaserB || this.teaser.textContent;
      window.addEventListener('message', (event) => this.onGiveawayMessage(event));
    }

    this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.querySelector('[data-close-link]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('cancel', () => this.remember(`closed:${Date.now()}`)); // Esc key
    this.teaser.addEventListener('click', () => this.open());

    this.querySelectorAll('[data-answer]').forEach((button) =>
      button.addEventListener('click', () => {
        this.answer = button.dataset.answer;
        this.showStep('email');
      })
    );
    this.querySelector('[data-step="email"]').addEventListener('submit', (event) => this.submitEmail(event));
    this.querySelector('[data-step="phone"]').addEventListener('submit', (event) => this.submitPhone(event));

    // Theme editor: show it while its section is selected
    if (this.hasAttribute('data-editor')) {
      document.addEventListener('shopify:section:select', (e) => e.target.contains(this) && this.open());
      document.addEventListener('shopify:section:deselect', (e) => e.target.contains(this) && this.dialog.close());
      return;
    }

    // Test links (?popup=a / ?popup=b) always show it, even to people Klaviyo already knows
    this.testing = /^[ab]$/.test(new URLSearchParams(location.search).get('popup') || '');

    const state = this.state();
    if (!this.testing && (state === 'signed-up' || this.cameFromEmail())) return;

    if (state.startsWith('closed:')) {
      const closedAt = Number(state.split(':')[1]);
      const days = Number(this.dataset.days) || 7;
      if (Date.now() - closedAt < days * 86400000) {
        this.teaser.hidden = false;
        return;
      }
    }

    // Version B: start loading the giveaway form now, so it's ready by the time the pop-up fades in
    if (this.variant === 'B') this.loadGiveaway();

    setTimeout(async () => {
      // Already on Klaviyo (signed up before, clicked one of our emails/texts...)? Never show it.
      if (!this.testing && (await this.knownToKlaviyo())) {
        this.remember('signed-up');
        return;
      }
      // Don't interrupt someone who has the cart drawer or another pop-up open
      if (document.querySelector('dialog[open]') || document.body.classList.contains('drawer-open')) {
        this.teaser.hidden = false;
        return;
      }
      this.open();
    }, Number(this.dataset.delay) || 0);
  }

  // Klaviyo's own script recognises people it already knows on this browser: anyone who has
  // signed up through a form, clicked a link in our emails/texts, etc. (works across sessions).
  async knownToKlaviyo() {
    try {
      if (window.klaviyo?.isIdentified) {
        const known = await Promise.race([
          window.klaviyo.isIdentified(),
          new Promise((resolve) => setTimeout(() => resolve(false), 1500)),
        ]);
        if (known) return true;
      }
      // Backup: Klaviyo's cookie holds an email/phone/id once someone is known
      const cookie = document.cookie.split('; ').find((c) => c.startsWith('__kla_id='));
      if (cookie) {
        const data = JSON.parse(atob(decodeURIComponent(cookie.split('=')[1])));
        return Boolean(data.$email || data.$phone_number || data.$exchange_id);
      }
    } catch {}
    return false;
  }

  // People arriving from a Klaviyo email/text are already on the list
  cameFromEmail() {
    const params = new URLSearchParams(location.search);
    return params.has('_kx') || /email|sms/i.test(params.get('utm_medium') || '');
  }

  state() {
    try {
      return localStorage.getItem(POPUP_KEY) || '';
    } catch {
      return '';
    }
  }

  remember(value) {
    try {
      localStorage.setItem(POPUP_KEY, value);
    } catch {}
    this.teaser.hidden = value === 'signed-up' || this.dialog.open;
  }

  // A/B test: each visitor gets A or B once (by the split %), then always the same one
  pickVariant() {
    const split = Number(this.dataset.abSplit);
    if (!this.dataset.giveawayUrl || Number.isNaN(split)) return 'A';
    try {
      // Testing: ?popup=a or ?popup=b picks that version for this browser (and shows it again)
      const forced = new URLSearchParams(location.search).get('popup');
      if (forced === 'a' || forced === 'b') {
        localStorage.setItem(VARIANT_KEY, forced.toUpperCase());
        localStorage.removeItem(POPUP_KEY);
        return forced.toUpperCase();
      }
      // Before the test's start time everyone gets A, without being assigned (so nobody is stuck on A)
      const start = Date.parse(this.dataset.abStart || '');
      if (start && Date.now() < start) return 'A';
      const saved = localStorage.getItem(VARIANT_KEY);
      if (saved === 'A' || saved === 'B') return saved;
      const picked = Math.random() * 100 < split ? 'B' : 'A';
      localStorage.setItem(VARIANT_KEY, picked);
      return picked;
    } catch {
      return 'A';
    }
  }

  // Version B: load the giveaway form the first time the pop-up opens
  loadGiveaway() {
    const frame = this.querySelector('[data-variant-b] iframe');
    if (frame.src) return;
    const url = new URL(this.dataset.giveawayUrl);
    url.searchParams.set('utm_source', 'sundayaus');
    url.searchParams.set('utm_medium', 'popup');
    url.searchParams.set('utm_campaign', 'welcome-popup-b');
    url.searchParams.set('bg', getComputedStyle(this).getPropertyValue('--popup-bg').trim().replace('#', '')); // same grey as version A
    frame.src = url.toString();
  }

  // Messages from the giveaway form (only what happened, never what was typed)
  onGiveawayMessage(event) {
    if (event.origin !== new URL(this.dataset.giveawayUrl).origin) return;
    if (event.data?.source !== 'sunday-giveaway') return;
    if (event.data.event === 'ready') this.querySelector('[data-variant-b] iframe').classList.add('is-ready');
    if (event.data.event === 'entered') this.remember('signed-up');
    if (event.data.event === 'dismiss') this.close(); // "nah i don't like free holidays"
    if (event.data.event === 'closed') {
      // Giveaway has ended: this visitor gets the $20 pop-up from now on
      try {
        localStorage.setItem(VARIANT_KEY, 'A');
      } catch {}
      this.variant = 'A';
      this.querySelector('[data-variant-b]').hidden = true;
      this.querySelector('[data-variant-a]').hidden = false;
    }
  }

  open() {
    this.teaser.hidden = true;
    if (this.variant === 'B') this.loadGiveaway();
    if (this.state() !== 'signed-up') this.showStep('offer');
    this.dialog.showModal();
    this.dialog.querySelector('[data-close]').blur();
  }

  close() {
    this.dialog.close();
    if (this.state() !== 'signed-up') this.remember(`closed:${Date.now()}`);
    this.teaser.hidden = this.state() === 'signed-up' || this.hasAttribute('data-editor');
  }

  showStep(name) {
    this.querySelectorAll('[data-step]').forEach((step) => (step.hidden = step.dataset.step !== name));
    const field = this.querySelector(`[data-step="${name}"] input`);
    if (field) setTimeout(() => field.focus(), 50);
  }

  // Klaviyo client API: subscribe this person to the list. Email marketing always; including a phone
  // number also subscribes them to text messages (Klaviyo works the consent out from the list).
  subscribe({ email, phone }) {
    const { klaviyoKey: key, klaviyoList: listId } = this.dataset;
    return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${key}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'welcome pop-up',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  ...(phone && { phone_number: phone }),
                  properties: {
                    'who are you shopping for today?': this.answer,
                    'welcome pop-up': true,
                    'welcome pop-up version': 'A',
                  },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      }),
    });
  }

  async submitEmail(event) {
    event.preventDefault();
    const form = event.target;
    const message = form.querySelector('[data-message]');
    const email = form.email.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.textContent = 'please enter a valid email.';
      return;
    }
    // Kept until the phone step: both are sent to Klaviyo together
    message.textContent = '';
    this.email = email;
    this.showStep('phone');
  }

  async submitPhone(event) {
    event.preventDefault();
    const form = event.target;
    const message = form.querySelector('[data-message]');
    if (!form.phone.value.trim()) {
      message.textContent = 'please enter your phone number.';
      return;
    }
    const phone = toInternationalPhone(form.phone.value, form.country_code?.value || this.dataset.countryCode || '61');
    if (!phone) {
      message.textContent = 'please check your phone number.';
      return;
    }
    const button = form.querySelector('button[type="submit"]');
    button.disabled = true;
    message.textContent = '';
    try {
      const response = await this.subscribe({ email: this.email, phone });
      if (!response.ok) throw new Error('klaviyo');
      // Let Klaviyo recognise this browser from now on (also stops the pop-up on their next visits)
      try {
        window.klaviyo?.identify({ email: this.email, phone_number: phone });
      } catch {}
      this.finish();
    } catch {
      message.textContent = 'something went wrong - please try again.';
    } finally {
      button.disabled = false;
    }
  }

  finish() {
    this.remember('signed-up');
    this.showStep('done');
  }
}

// "0412 345 678" -> "+61412345678" (same rule as the notify-me and password page forms)
function toInternationalPhone(raw, countryCode) {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, '');
  let full;
  if (trimmed.startsWith('+')) full = `+${digits}`;
  else if (digits.startsWith('00')) full = `+${digits.slice(2)}`;
  else if (digits.startsWith(countryCode)) full = `+${digits}`;
  else full = `+${countryCode}${digits.replace(/^0/, '')}`;
  return /^\+\d{8,15}$/.test(full) ? full : '';
}

customElements.define('welcome-popup', WelcomePopup);
